import { type ReactNode, useMemo } from 'react'
import { IntlProvider } from 'react-intl'
import type { PageContextPropsType } from '../../../../i18n/i18n'
import { STORE, usePreferredLanguagesSettingStore } from '../../../../i18n/usePreferredLanguagesSetting'
import { useEnglishGlobalNavigationEntity } from '../../../utils/use-english-global-navigation-entity'
import { useGlobalNavigationEntity } from '../../../utils/use-global-navigation-entity'
import Footer from '../Footer'
import LayoutProvider from '../LayoutProvider'
import HeaderDesktop from '../header/HeaderDesktop'
import HeaderMobile from '../header/HeaderMobile'
import 'ress'
import '../../../styles/foundation/Initialize.css'
import { useLocation } from '@reach/router'
import { useLocalizedPath } from '../../../../i18n/useLocalizedPath'
import * as LayoutStyles from '../../../styles/foundation/Layout.css'

interface LayoutProps extends PageContextPropsType {
  children: ReactNode
  globalNavigationPreviewEntity?: Queries.Hygraph_GlobalNavigation
  footerBg?: string
}

export const Layout = ({ children, globalNavigationPreviewEntity, footerBg, pageContext }: LayoutProps) => {
  const language = pageContext.language
  const messages = pageContext.messages
  const { preferredLanguage } = usePreferredLanguagesSettingStore()

  /* biome-ignore lint/correctness/useHookAtTopLevel: Biomeを導入したときに、エラーを検知したがルールを有効にしたいのでignoreコメントを追加している */
  const globalNavigationEntity = globalNavigationPreviewEntity || useGlobalNavigationEntity()
  /* biome-ignore lint/correctness/useHookAtTopLevel: Biomeを導入したときに、エラーを検知したがルールを有効にしたいのでignoreコメントを追加している */
  const englishGlobalNavigationEntity = globalNavigationPreviewEntity || useEnglishGlobalNavigationEntity()

  const LocalizedNavigationContent = ({ children }: { children: ReactNode }) => {
    const showEnglishPage = useLocalizedPath()
    const location = useLocation()

    /**
     * Maps English content to Japanese content with fallback
     * @param englishItem - The English content item
     * @param japaneseItem - The Japanese content item to use as fallback
     */
    const mapLocalizedContent = <T extends { label?: string; link?: string; title?: string }>(
      englishItem: T | undefined | null,
      japaneseItem: T
    ): T => ({
      ...japaneseItem,
      ...(englishItem?.label && { label: englishItem.label }),
      ...(englishItem?.link && { link: englishItem.link }),
      ...(englishItem?.title && { title: englishItem.title }),
    })

    /**
     * Maps thumbnail content with English override
     * @param englishThumbnail - The English thumbnail
     * @param japaneseThumbnail - The Japanese thumbnail to use as fallback
     */
    const mapThumbnail = (englishThumbnail: { url: string } | undefined | null, japaneseThumbnail: any) =>
      englishThumbnail?.url ? { ...englishThumbnail } : japaneseThumbnail

    /**
     * Maps a list of items with English overrides
     * @param japaneseList - The Japanese list to use as base
     * @param englishList - The English list to use for overrides
     * @param mapper - Optional custom mapper function
     */
    const mapLocalizedList = <T extends Record<string, any>>(
      japaneseList: T[],
      englishList: T[] | undefined,
      mapper?: (eng: T | undefined, jp: T, index: number) => T
    ): T[] =>
      japaneseList.map((item, index) =>
        mapper ? mapper(englishList?.[index], item, index) : mapLocalizedContent(englishList?.[index], item)
      )

    const localizedNavigationEntity = useMemo(() => {
      if (preferredLanguage === STORE.PREFFERRED_LANGUAGE.JAPANESE || !showEnglishPage) {
        return globalNavigationEntity
      }

      return {
        ...globalNavigationEntity,
        package:
          globalNavigationEntity.package && englishGlobalNavigationEntity.package
            ? {
                ...globalNavigationEntity.package,
                linkListing: mapLocalizedList(
                  globalNavigationEntity.package.linkListing,
                  englishGlobalNavigationEntity.package?.linkListing,
                  (eng, jp) => ({
                    ...jp,
                    name: eng?.name ?? jp.name,
                    copy: eng?.copy ?? jp.name,
                  })
                ),
              }
            : globalNavigationEntity.package,
        feature:
          globalNavigationEntity.feature && englishGlobalNavigationEntity.feature
            ? {
                ...globalNavigationEntity.feature,
                linkGroupListing: mapLocalizedList(
                  globalNavigationEntity.feature.linkGroupListing,
                  englishGlobalNavigationEntity.feature?.linkGroupListing,
                  (eng, jp) => ({
                    ...jp,
                    headline: eng?.headline ?? jp.headline,
                    linkListing: mapLocalizedList(jp.linkListing, eng?.linkListing),
                  })
                ),
              }
            : globalNavigationEntity.feature,
        industry:
          globalNavigationEntity.industry && englishGlobalNavigationEntity.industry
            ? {
                ...globalNavigationEntity.industry,
                linkListing: mapLocalizedList(
                  globalNavigationEntity.industry.linkListing,
                  englishGlobalNavigationEntity.industry?.linkListing
                ),
              }
            : globalNavigationEntity.industry,
        interview:
          globalNavigationEntity.interview && englishGlobalNavigationEntity.interview
            ? {
                ...globalNavigationEntity.interview,
                linkListing: mapLocalizedList(
                  globalNavigationEntity.interview.linkListing,
                  englishGlobalNavigationEntity.interview?.linkListing,
                  (eng, jp) => ({
                    ...jp,
                    title: eng?.title ?? jp.title,
                    thumbnail: mapThumbnail(eng?.thumbnail, jp.thumbnail),
                  })
                ),
              }
            : globalNavigationEntity.interview,
        caseStudy:
          globalNavigationEntity.caseStudy && englishGlobalNavigationEntity.caseStudy
            ? {
                ...globalNavigationEntity.caseStudy,
                linkListing: mapLocalizedList(
                  globalNavigationEntity.caseStudy.linkListing,
                  englishGlobalNavigationEntity.caseStudy?.linkListing,
                  (eng, jp) => ({
                    ...jp,
                    title: eng?.title ?? jp.title,
                    thumbnail: mapThumbnail(eng?.thumbnail, jp.thumbnail),
                  })
                ),
              }
            : globalNavigationEntity.caseStudy,
        seminar:
          globalNavigationEntity.seminar && englishGlobalNavigationEntity.seminar
            ? {
                ...globalNavigationEntity.seminar,
                linkListing: mapLocalizedList(
                  globalNavigationEntity.seminar.linkListing,
                  englishGlobalNavigationEntity.seminar?.linkListing
                ),
                recommend: {
                  ...globalNavigationEntity.seminar.recommend,
                  buttonLabel:
                    englishGlobalNavigationEntity.seminar.recommend?.buttonLabel ??
                    globalNavigationEntity.seminar.recommend.buttonLabel,
                  subTitle:
                    englishGlobalNavigationEntity.seminar.recommend?.subTitle ??
                    globalNavigationEntity.seminar.recommend.subTitle,
                  title:
                    englishGlobalNavigationEntity.seminar.recommend?.title ??
                    globalNavigationEntity.seminar.recommend.title,
                  summary:
                    englishGlobalNavigationEntity.seminar.recommend?.summary ??
                    globalNavigationEntity.seminar.recommend.summary,
                  thumbnail: mapThumbnail(
                    englishGlobalNavigationEntity.seminar.recommend?.thumbnail,
                    globalNavigationEntity.seminar.recommend.thumbnail
                  ),
                },
              }
            : globalNavigationEntity.seminar,
        ebook:
          globalNavigationEntity.ebook && englishGlobalNavigationEntity.ebook
            ? {
                ...globalNavigationEntity.ebook,
                linkListing: mapLocalizedList(
                  globalNavigationEntity.ebook.linkListing,
                  englishGlobalNavigationEntity.ebook?.linkListing
                ),
                indexLink: englishGlobalNavigationEntity.ebook.indexLink ?? globalNavigationEntity.ebook.indexLink,
                recommend: {
                  ...globalNavigationEntity.ebook.recommend,
                  buttonLabel:
                    englishGlobalNavigationEntity.ebook.recommend?.buttonLabel ??
                    globalNavigationEntity.ebook.recommend.buttonLabel,
                  link:
                    englishGlobalNavigationEntity.ebook.recommend?.link ?? globalNavigationEntity.ebook.recommend.link,
                  subTitle:
                    englishGlobalNavigationEntity.ebook.recommend?.subTitle ??
                    globalNavigationEntity.ebook.recommend.subTitle,
                  summary:
                    englishGlobalNavigationEntity.ebook.recommend?.summary ??
                    globalNavigationEntity.ebook.recommend.summary,
                  title:
                    englishGlobalNavigationEntity.ebook.recommend?.title ??
                    globalNavigationEntity.ebook.recommend.title,
                  thumbnail: mapThumbnail(
                    englishGlobalNavigationEntity.ebook.recommend?.thumbnail,
                    globalNavigationEntity.ebook.recommend.thumbnail
                  ),
                },
              }
            : globalNavigationEntity.ebook,
        support: mapLocalizedList(globalNavigationEntity.support, englishGlobalNavigationEntity.support),
        spec: mapLocalizedList(globalNavigationEntity.spec, englishGlobalNavigationEntity.spec),
        operationalSupport:
          globalNavigationEntity.operationalSupport && englishGlobalNavigationEntity.operationalSupport
            ? {
                ...globalNavigationEntity.operationalSupport,
                linkListing: mapLocalizedList(
                  globalNavigationEntity.operationalSupport.linkListing,
                  englishGlobalNavigationEntity.operationalSupport?.linkListing
                ),
                thumbnail: mapThumbnail(
                  englishGlobalNavigationEntity.operationalSupport.thumbnail,
                  globalNavigationEntity.operationalSupport.thumbnail
                ),
                thumbnailLink:
                  englishGlobalNavigationEntity.operationalSupport.thumbnailLink ??
                  globalNavigationEntity.operationalSupport.thumbnailLink,
              }
            : globalNavigationEntity.operationalSupport,
      }
    }, [showEnglishPage, preferredLanguage, location.pathname, globalNavigationEntity, englishGlobalNavigationEntity])

    return (
      <div className={LayoutStyles.Wrapper}>
        <HeaderMobile globalNavigationEntity={localizedNavigationEntity} />
        <HeaderDesktop globalNavigationEntity={localizedNavigationEntity} />
        {children}
        <Footer globalNavigationEntity={localizedNavigationEntity} backgroundColor={footerBg} />
      </div>
    )
  }

  return (
    <IntlProvider locale={language} messages={messages}>
      <LayoutProvider>
        <LocalizedNavigationContent>{children}</LocalizedNavigationContent>
      </LayoutProvider>
    </IntlProvider>
  )
}
